<template>
    <div>
        <Breadcrumbs main="Service Management" :title="isEditMode ? 'Edit Buy Back Device' : 'Add Buy Back Device'" />
        <div class="container-fluid">
            <div class="row">
                <div class="col-sm-12">
                    <div class="card">
                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-12">
                                    <router-link :to="{ name: 'buy_back_devices' }" class="btn btn-primary float-end">
                                        <i class='bx bx-chevrons-left'></i> Back
                                    </router-link>
                                </div>
                            </div>

                            <form @submit.prevent="submitForm" enctype="multipart/form-data">
                                <div class="form theme-form">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label class="control-label">Device Type<span class="text-danger">*</span></label>
                                                <select class="form-select" v-model="formData.device_type" @change="onDeviceTypeChange" :disabled="isEditMode" :readonly="isEditMode">
                                                    <option value="">--Select--</option>
                                                    <option value="1">Old</option>
                                                    <option value="2">Both</option>
                                                </select>
                                                <div v-if="errors.device_type" class="text-danger">{{ errors.device_type }}</div>
                                            </div>
                                        </div>

                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label class="control-label">Device Name</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    placeholder="Enter device name" 
                                                    v-model="formData.device_name"
                                                    :disabled="isEditMode && formData.device_name"
                                                    :readonly="isEditMode && formData.device_name"
                                                />
                                                <div v-if="errors.device_name" class="text-danger">{{ errors.device_name }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Old Device Section (Type 1 Old, Type 2 Both) -->
                                    <div v-if="showOldDevice">
                                        <div class="row mb-2" v-if="formData.device_type == '2'">
                                            <div class="col-12">
                                                <label class="form-label fw-semibold text-secondary">Old device details</label>
                                            </div>
                                        </div>
                                        <div class="row">
                                        <div class="col-md-6">
                                            <div class="mb-3 position-relative">
                                                <label class="control-label">Old Device</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    placeholder="Search by IMEI" 
                                                    v-model="oldDeviceSearch" 
                                                    @input="searchOldDevice" 
                                                    @focus="showOldDeviceSuggestions = true" 
                                                    @blur="hideOldDeviceSuggestions"
                                                    :disabled="isEditMode && hasExistingOldDevice"
                                                    :readonly="isEditMode && hasExistingOldDevice"
                                                    autocomplete="off"
                                                />
                                                <div v-if="errors.old_device_id || errors.old_device_imei" class="text-danger">
                                                    {{ errors.old_device_id || errors.old_device_imei }}
                                                </div>
                                                <ul v-if="showOldDeviceSuggestions && filteredOldDevices.length" 
                                                    class="list-group position-absolute w-100" 
                                                    style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                    <li v-for="device in filteredOldDevices" 
                                                        :key="device.id" 
                                                        class="list-group-item list-group-item-action" 
                                                        @mousedown.prevent="selectOldDevice(device)">
                                                        <div><strong>IMEI:</strong> {{ device.imei }}</div>
                                                        <div><strong>ICCID:</strong> {{ device.iccid }}</div>
                                                        <div v-if="device.dealer_name"><strong>Dealer:</strong> {{ device.dealer_name }}</div>
                                                    </li>
                                                </ul>
                                                <div v-if="selectedOldDevice" class="mt-2">
                                                    <small class="text-success">
                                                        <i class="icofont icofont-check-circled me-1"></i>
                                                        Selected: <strong>{{ selectedOldDevice.imei }}</strong>
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label class="control-label">Old Device IMEI</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    placeholder="Enter IMEI manually" 
                                                    v-model="formData.old_device_imei"
                                                    :readonly="(isEditMode && hasExistingOldDevice) || selectedOldDevice !== null"
                                                    :disabled="isEditMode && hasExistingOldDevice"
                                                />
                                            </div>
                                        </div>
                                        </div>
                                        <!-- Old device current dealer & new dealer (Type 2 only) -->
                                        <div class="row" v-if="formData.device_type == '2'">
                                            <div class="col-md-6">
                                                <div class="mb-3">
                                                    <label class="control-label">Old device current dealer</label>
                                                    <input 
                                                        class="form-control" 
                                                        type="text" 
                                                        :value="currentDealerDisplay" 
                                                        disabled
                                                    />
                                                </div>
                                            </div>
                                            <div class="col-md-6">
                                                <div class="mb-3 position-relative">
                                                    <label class="control-label">Old device new dealer</label>
                                                    <input 
                                                        class="form-control" 
                                                        type="text" 
                                                        placeholder="Search dealer" 
                                                        v-model="dealerSearch" 
                                                        @input="searchDealer" 
                                                        @focus="onDealerFocus" 
                                                        @blur="hideDealerSuggestions"
                                                        :disabled="isEditMode && formData.dealer_id"
                                                        :readonly="isEditMode && formData.dealer_id"
                                                        autocomplete="off"
                                                    />
                                                    <div v-if="errors.dealer_id" class="text-danger">{{ errors.dealer_id }}</div>
                                                    <ul v-if="showDealerSuggestions && dealers.length > 0" 
                                                        class="list-group position-absolute w-100" 
                                                        style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                        <li v-for="dealer in (dealerSearch.trim() === '' ? dealers : filteredDealers)" 
                                                            :key="dealer.id" 
                                                            class="list-group-item list-group-item-action" 
                                                            @mousedown.prevent="selectDealer(dealer)">
                                                            <div><strong>{{ dealer.first_name }}</strong></div>
                                                        </li>
                                                        <li v-if="dealerSearch.trim() !== '' && filteredDealers.length === 0" class="list-group-item">
                                                            <div class="text-muted">No dealers found</div>
                                                        </li>
                                                    </ul>
                                                    <ul v-if="showDealerSuggestions && dealers.length === 0" 
                                                        class="list-group position-absolute w-100" 
                                                        style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                        <li class="list-group-item">
                                                            <div class="text-muted">No dealers available</div>
                                                        </li>
                                                    </ul>
                                                    <div v-if="selectedDealer" class="mt-2">
                                                        <small class="text-success">
                                                            <i class="icofont icofont-check-circled me-1"></i>
                                                            Selected: <strong>{{ selectedDealer.first_name }}</strong>
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- New Device Section (Type 2 Both) -->
                                    <div v-if="showNewDevice">
                                        <div class="row mb-2" v-if="formData.device_type == '2'">
                                            <div class="col-12">
                                                <label class="form-label fw-semibold text-secondary">New device details</label>
                                            </div>
                                        </div>
                                        <div class="row">
                                        <div class="col-md-6">
                                            <div class="mb-3 position-relative">
                                                <label class="control-label">New Device</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    placeholder="Search by IMEI" 
                                                    v-model="newDeviceSearch" 
                                                    @input="searchNewDevice" 
                                                    @focus="showNewDeviceSuggestions = true" 
                                                    @blur="hideNewDeviceSuggestions"
                                                    :disabled="isEditMode && hasExistingNewDevice"
                                                    :readonly="isEditMode && hasExistingNewDevice"
                                                    autocomplete="off"
                                                />
                                                <div v-if="errors.new_device_id || errors.new_device_imei" class="text-danger">
                                                    {{ errors.new_device_id || errors.new_device_imei }}
                                                </div>
                                                <ul v-if="showNewDeviceSuggestions && filteredNewDevices.length" 
                                                    class="list-group position-absolute w-100" 
                                                    style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                    <li v-for="device in filteredNewDevices" 
                                                        :key="device.id" 
                                                        class="list-group-item list-group-item-action" 
                                                        @mousedown.prevent="selectNewDevice(device)">
                                                        <div><strong>IMEI:</strong> {{ device.imei }}</div>
                                                        <div><strong>ICCID:</strong> {{ device.iccid }}</div>
                                                    </li>
                                                </ul>
                                                <div v-if="selectedNewDevice" class="mt-2">
                                                    <small class="text-success">
                                                        <i class="icofont icofont-check-circled me-1"></i>
                                                        Selected: <strong>{{ selectedNewDevice.imei }}</strong>
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label class="control-label">New Device IMEI</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    placeholder="Enter IMEI manually" 
                                                    v-model="formData.new_device_imei"
                                                    :readonly="(isEditMode && hasExistingNewDevice) || selectedNewDevice !== null"
                                                    :disabled="isEditMode && hasExistingNewDevice"
                                                />
                                            </div>
                                        </div>
                                        </div>
                                    </div>

                                    <!-- Dealer Section: Type 2 (Both) = new device dealer only (old device dealers are in Old device details) -->
                                    <div class="row" v-if="formData.device_type == '2'">
                                        <div class="col-md-12">
                                            <div class="mb-3 position-relative">
                                                <label class="control-label">New device dealer</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    placeholder="Search dealer" 
                                                    v-model="newDeviceDealerSearch" 
                                                    @input="searchNewDeviceDealer" 
                                                    @focus="onNewDeviceDealerFocus" 
                                                    @blur="hideNewDeviceDealerSuggestions"
                                                    :disabled="isEditMode && formData.new_device_dealer_id"
                                                    :readonly="isEditMode && formData.new_device_dealer_id"
                                                    autocomplete="off"
                                                />
                                                <div v-if="errors.new_device_dealer_id" class="text-danger">{{ errors.new_device_dealer_id }}</div>
                                                <ul v-if="showNewDeviceDealerSuggestions && dealers.length > 0" 
                                                    class="list-group position-absolute w-100" 
                                                    style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                    <li v-for="dealer in (newDeviceDealerSearch.trim() === '' ? dealers : filteredNewDeviceDealers)" 
                                                        :key="'nd-' + dealer.id" 
                                                        class="list-group-item list-group-item-action" 
                                                        @mousedown.prevent="selectNewDeviceDealer(dealer)">
                                                        <div><strong>{{ dealer.first_name }}</strong></div>
                                                    </li>
                                                    <li v-if="newDeviceDealerSearch.trim() !== '' && filteredNewDeviceDealers.length === 0" class="list-group-item">
                                                        <div class="text-muted">No dealers found</div>
                                                    </li>
                                                </ul>
                                                <ul v-if="showNewDeviceDealerSuggestions && dealers.length === 0" 
                                                    class="list-group position-absolute w-100" 
                                                    style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                    <li class="list-group-item">
                                                        <div class="text-muted">No dealers available</div>
                                                    </li>
                                                </ul>
                                                <div v-if="selectedNewDeviceDealer" class="mt-2">
                                                    <small class="text-success">
                                                        <i class="icofont icofont-check-circled me-1"></i>
                                                        Selected: <strong>{{ selectedNewDeviceDealer.first_name }}</strong>
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Dealer Section: Type 1 or 3 -->
                                    <div class="row" v-else>
                                        <div class="col-md-6" v-if="showOldDevice">
                                            <div class="mb-3">
                                                <label class="control-label">Current dealer</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    :value="currentDealerDisplay" 
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="mb-3 position-relative">
                                                <label class="control-label">{{ showOldDevice ? 'New dealer' : 'Dealer' }}</label>
                                                <input 
                                                    class="form-control" 
                                                    type="text" 
                                                    placeholder="Search dealer" 
                                                    v-model="dealerSearch" 
                                                    @input="searchDealer" 
                                                    @focus="onDealerFocus" 
                                                    @blur="hideDealerSuggestions"
                                                    :disabled="isEditMode && formData.dealer_id"
                                                    :readonly="isEditMode && formData.dealer_id"
                                                    autocomplete="off"
                                                />
                                                <div v-if="errors.dealer_id" class="text-danger">{{ errors.dealer_id }}</div>
                                                <ul v-if="showDealerSuggestions && dealers.length > 0" 
                                                    class="list-group position-absolute w-100" 
                                                    style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                    <li v-for="dealer in (dealerSearch.trim() === '' ? dealers : filteredDealers)" 
                                                        :key="dealer.id" 
                                                        class="list-group-item list-group-item-action" 
                                                        @mousedown.prevent="selectDealer(dealer)">
                                                        <div><strong>{{ dealer.first_name }}</strong></div>
                                                    </li>
                                                    <li v-if="dealerSearch.trim() !== '' && filteredDealers.length === 0" class="list-group-item">
                                                        <div class="text-muted">No dealers found</div>
                                                    </li>
                                                </ul>
                                                <ul v-if="showDealerSuggestions && dealers.length === 0" 
                                                    class="list-group position-absolute w-100" 
                                                    style="z-index: 10; max-height: 200px; overflow-y: auto;">
                                                    <li class="list-group-item">
                                                        <div class="text-muted">No dealers available</div>
                                                    </li>
                                                </ul>
                                                <div v-if="selectedDealer" class="mt-2">
                                                    <small class="text-success">
                                                        <i class="icofont icofont-check-circled me-1"></i>
                                                        Selected: <strong>{{ selectedDealer.first_name }}</strong>
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6" v-if="showOldDevice && (formData.device_type == '1' || formData.device_type == '3') || formData.device_type == '2'">
                                            <div class="mb-3">
                                                <label class="control-label">Received Date</label>
                                                <input 
                                                    class="form-control" 
                                                    type="date" 
                                                    v-model="formData.received_date"
                                                    :disabled="isEditMode && formData.received_date"
                                                    :readonly="isEditMode && formData.received_date"
                                                />
                                                <div v-if="errors.received_date" class="text-danger">{{ errors.received_date }}</div>
                                            </div>
                                        </div>
                                        <div class="col-md-6" v-if="showNewDevice && (formData.device_type == '2' || formData.device_type == '3')">
                                            <div class="mb-3">
                                                <label class="control-label">Allocated Date</label>
                                                <input 
                                                    class="form-control" 
                                                    type="date" 
                                                    v-model="formData.allocated_date"
                                                    :disabled="isEditMode && formData.allocated_date"
                                                    :readonly="isEditMode && formData.allocated_date"
                                                />
                                                <div v-if="errors.allocated_date" class="text-danger">{{ errors.allocated_date }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="row mt-3">
                                        <div class="col-sm-12">
                                            <button class="btn btn-success me-2 float-end" type="submit" :disabled="isSubmitting">
                                                <span v-if="isSubmitting">
                                                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {{ isEditMode ? 'Updating...' : 'Saving...' }}
                                                </span>
                                                <span v-else>{{ isEditMode ? 'Update' : 'Save' }}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "Add Buy Back Device",
    
    data() {
        return {
            isEditMode: false,
            isSubmitting: false,
            token: "",
            errors: {},
            
            formData: {
                id: null,
                device_type: "",
                old_device_id: null,
                new_device_id: null,
                old_device_imei: "",
                new_device_imei: "",
                dealer_id: null,
                new_device_dealer_id: null,
                company_id: null,
                device_name: "",
                allocated_date: "",
                received_date: "",
            },

            // Old Device Search
            oldDeviceSearch: "",
            oldDevices: [],
            filteredOldDevices: [],
            showOldDeviceSuggestions: false,
            selectedOldDevice: null,

            // New Device Search
            newDeviceSearch: "",
            newDevices: [],
            filteredNewDevices: [],
            showNewDeviceSuggestions: false,
            selectedNewDevice: null,

            // Dealer Search
            dealerSearch: "",
            dealers: [],
            filteredDealers: [],
            showDealerSuggestions: false,
            selectedDealer: null,

            // New device dealer (type 2 only)
            newDeviceDealerSearch: "",
            filteredNewDeviceDealers: [],
            showNewDeviceDealerSuggestions: false,
            selectedNewDeviceDealer: null,

            // Debounce timers for search (avoids lag on every keystroke)
            searchOldDeviceTimer: null,
            searchNewDeviceTimer: null,
            searchDealerTimer: null,
            searchNewDeviceDealerTimer: null,
        };
    },

    beforeUnmount() {
        if (this.searchOldDeviceTimer) clearTimeout(this.searchOldDeviceTimer);
        if (this.searchNewDeviceTimer) clearTimeout(this.searchNewDeviceTimer);
        if (this.searchDealerTimer) clearTimeout(this.searchDealerTimer);
        if (this.searchNewDeviceDealerTimer) clearTimeout(this.searchNewDeviceDealerTimer);
    },

    computed: {
        hasExistingOldDevice() {
            return !!(this.formData.old_device_imei || this.formData.old_device_id);
        },
        hasExistingNewDevice() {
            return !!(this.formData.new_device_imei || this.formData.new_device_id);
        },
        showOldDevice() {
            if (this.isEditMode) {
                // In edit mode, show if old device exists OR if new device exists (to allow adding old)
                return this.hasExistingOldDevice || this.hasExistingNewDevice;
            }
            return this.formData.device_type == "1" || this.formData.device_type == "2" || this.formData.device_type == "3";
        },
        showNewDevice() {
            if (this.isEditMode) {
                // In edit mode, show if new device exists OR if old device exists (to allow adding new)
                return this.hasExistingNewDevice || this.hasExistingOldDevice;
            }
            return this.formData.device_type == "2" || this.formData.device_type == "3";
        },
        currentDealerDisplay() {
            if (!this.selectedOldDevice) return 'Select old device first';
            return this.selectedOldDevice.dealer_name || '—';
        },
    },

    created() {
        this.getToken();
        if (this.$route.params.id) {
            this.isEditMode = true;
            this.formData.id = parseInt(this.$route.params.id);
            this.loadEditData();
        }
        this.fetchOldDevices();
        this.fetchNewDevices();
        this.fetchDealers();
    },

    methods: {
        getToken() {
            if (localStorage.getItem('airotrack_staff')) {
                var airotrack_staff = JSON.parse(localStorage.getItem('airotrack_staff'));
                if (airotrack_staff.token) {
                    this.token = airotrack_staff.token;
                    this.user = airotrack_staff;
                } else {
                    this.$swal("", "Failed to fetch token, please login again", "error");
                    this.$router.push({ name: "login" });
                }
            } else {
                this.$swal("", "Session expired, please login again", "error");
                this.$router.push({ name: "login" });
            }
        },

        loadEditData() {
            const query = this.$route.query;
            
            // Determine device type based on existing data
            if (query.old_device_imei && query.new_device_imei) {
                this.formData.device_type = "3"; // Both
            } else if (query.old_device_imei) {
                this.formData.device_type = "1"; // Old
            } else if (query.new_device_imei) {
                this.formData.device_type = "2"; // New
            }

            // Populate form data
            if (query.old_device_imei) {
                this.formData.old_device_imei = query.old_device_imei;
                this.oldDeviceSearch = query.old_device_imei;
            }
            
            if (query.new_device_imei) {
                this.formData.new_device_imei = query.new_device_imei;
                this.newDeviceSearch = query.new_device_imei;
            }
            
            if (query.device_name) {
                this.formData.device_name = query.device_name;
            }
            
            if (query.dealer_id) {
                this.formData.dealer_id = parseInt(query.dealer_id);
                if (query.dealer_name) {
                    this.dealerSearch = query.dealer_name;
                    // Try to find and select the dealer
                    this.$nextTick(() => {
                        const dealer = this.dealers.find(d => d.id === this.formData.dealer_id);
                        if (dealer) {
                            this.selectedDealer = dealer;
                        }
                    });
                }
            }
            
            if (query.allocated_date) {
                this.formData.allocated_date = query.allocated_date;
            }
            if (query.received_date) {
                this.formData.received_date = query.received_date;
            }
            if (query.new_device_dealer_id) {
                this.formData.new_device_dealer_id = parseInt(query.new_device_dealer_id);
                if (query.new_device_dealer_name) {
                    this.newDeviceDealerSearch = query.new_device_dealer_name;
                }
                this.$nextTick(() => {
                    const dealer = this.dealers.find(d => d.id === this.formData.new_device_dealer_id);
                    if (dealer) {
                        this.selectedNewDeviceDealer = dealer;
                        this.newDeviceDealerSearch = dealer.first_name;
                    }
                });
            }
        },

        onDeviceTypeChange() {
            // Reset selections when device type changes (only in add mode)
            if (!this.isEditMode) {
                if (this.formData.device_type != "1" && this.formData.device_type != "3") {
                    this.selectedOldDevice = null;
                    this.formData.old_device_id = null;
                    this.formData.old_device_imei = "";
                    this.oldDeviceSearch = "";
                }
                if (this.formData.device_type != "2" && this.formData.device_type != "3") {
                    this.selectedNewDevice = null;
                    this.formData.new_device_id = null;
                    this.formData.new_device_imei = "";
                    this.newDeviceSearch = "";
                    this.selectedDealer = null;
                    this.formData.dealer_id = null;
                    this.dealerSearch = "";
                    this.selectedNewDeviceDealer = null;
                    this.formData.new_device_dealer_id = null;
                    this.newDeviceDealerSearch = "";
                    this.formData.allocated_date = "";
                }
                if (this.formData.device_type != "1" && this.formData.device_type != "3") {
                    this.formData.received_date = "";
                }
            }
        },

        // Old Device Methods
        fetchOldDevices() {
            this.$axios
                .get("/staffs/fetchAllocatedDevices", { 
                    headers: { Authorization: "Bearer " + this.token } 
                })
                .then((res) => {
                    if (res.data.status === 'true' || res.data.status === true) {
                        this.oldDevices = res.data.data || [];
                    } else {
                        this.oldDevices = [];
                    }
                })
                .catch((err) => {
                    console.error('Error fetching old devices:', err);
                    this.oldDevices = [];
                });
        },

        searchOldDevice() {
            if (this.searchOldDeviceTimer) clearTimeout(this.searchOldDeviceTimer);
            if (!this.oldDeviceSearch.trim()) {
                this.filteredOldDevices = [];
                return;
            }
            this.searchOldDeviceTimer = setTimeout(() => {
                const searchTerm = this.oldDeviceSearch.trim().toLowerCase();
                if (!searchTerm) {
                    this.filteredOldDevices = [];
                    this.searchOldDeviceTimer = null;
                    return;
                }
                this.filteredOldDevices = this.oldDevices
                    .filter(device => device.imei && device.imei.toLowerCase().includes(searchTerm))
                    .slice(0, 50);
                this.searchOldDeviceTimer = null;
            }, 200);
        },

        selectOldDevice(device) {
            this.selectedOldDevice = device;
            this.formData.old_device_id = device.id;
            this.formData.old_device_imei = device.imei;
            this.oldDeviceSearch = device.imei;
            this.showOldDeviceSuggestions = false;
        },

        hideOldDeviceSuggestions() {
            setTimeout(() => {
                this.showOldDeviceSuggestions = false;
            }, 200);
        },

        // New Device Methods
        fetchNewDevices() {
            this.$axios
                .get("/staffs/fetchDisallocatedDevices", { 
                    headers: { Authorization: "Bearer " + this.token } 
                })
                .then((res) => {
                    if (res.data.status === 'true' || res.data.status === true) {
                        this.newDevices = res.data.data || [];
                    } else {
                        this.newDevices = [];
                    }
                })
                .catch((err) => {
                    console.error('Error fetching new devices:', err);
                    this.newDevices = [];
                });
        },

        searchNewDevice() {
            if (this.searchNewDeviceTimer) clearTimeout(this.searchNewDeviceTimer);
            if (!this.newDeviceSearch.trim()) {
                this.filteredNewDevices = [];
                return;
            }
            this.searchNewDeviceTimer = setTimeout(() => {
                const searchTerm = this.newDeviceSearch.trim().toLowerCase();
                if (!searchTerm) {
                    this.filteredNewDevices = [];
                    this.searchNewDeviceTimer = null;
                    return;
                }
                this.filteredNewDevices = this.newDevices
                    .filter(device => device.imei && device.imei.toLowerCase().includes(searchTerm))
                    .slice(0, 50);
                this.searchNewDeviceTimer = null;
            }, 200);
        },

        selectNewDevice(device) {
            this.selectedNewDevice = device;
            this.formData.new_device_id = device.id;
            this.formData.new_device_imei = device.imei;
            this.newDeviceSearch = device.imei;
            this.showNewDeviceSuggestions = false;
        },

        hideNewDeviceSuggestions() {
            setTimeout(() => {
                this.showNewDeviceSuggestions = false;
            }, 200);
        },

        // Dealer Methods
        fetchDealers() {
            this.$axios
                .get("/staffs/fetchDealers", { 
                    headers: { Authorization: "Bearer " + this.token } 
                })
                .then((res) => {
                    if (res.data.status === 'true' || res.data.status === true) {
                        this.dealers = res.data.data.dealers || [];
                    } else {
                        this.dealers = [];
                    }
                })
                .catch((err) => {
                    console.error('Error fetching dealers:', err);
                    this.dealers = [];
                });
        },

        onDealerFocus() {
            this.showDealerSuggestions = true;
            // Initialize with all dealers when focused
            if (!this.dealerSearch.trim()) {
                this.filteredDealers = [...this.dealers];
            } else {
                this.searchDealer();
            }
        },

        searchDealer() {
            if (this.searchDealerTimer) clearTimeout(this.searchDealerTimer);
            if (!this.dealerSearch.trim()) {
                this.filteredDealers = [...this.dealers];
                return;
            }
            this.searchDealerTimer = setTimeout(() => {
                const searchTerm = this.dealerSearch.trim().toLowerCase();
                if (!searchTerm) {
                    this.filteredDealers = [...this.dealers];
                    this.searchDealerTimer = null;
                    return;
                }
                this.filteredDealers = this.dealers
                    .filter(dealer => dealer.first_name && dealer.first_name.toLowerCase().includes(searchTerm))
                    .slice(0, 50);
                this.searchDealerTimer = null;
            }, 200);
        },

        selectDealer(dealer) {
            this.selectedDealer = dealer;
            this.formData.dealer_id = dealer.id;
            this.dealerSearch = dealer.first_name;
            this.showDealerSuggestions = false;
        },

        hideDealerSuggestions() {
            setTimeout(() => {
                this.showDealerSuggestions = false;
            }, 200);
        },

        onNewDeviceDealerFocus() {
            this.showNewDeviceDealerSuggestions = true;
            if (!this.newDeviceDealerSearch.trim()) {
                this.filteredNewDeviceDealers = [...this.dealers];
            } else {
                this.searchNewDeviceDealer();
            }
        },

        searchNewDeviceDealer() {
            if (this.searchNewDeviceDealerTimer) clearTimeout(this.searchNewDeviceDealerTimer);
            if (!this.newDeviceDealerSearch.trim()) {
                this.filteredNewDeviceDealers = [...this.dealers];
                return;
            }
            this.searchNewDeviceDealerTimer = setTimeout(() => {
                const searchTerm = this.newDeviceDealerSearch.trim().toLowerCase();
                if (!searchTerm) {
                    this.filteredNewDeviceDealers = [...this.dealers];
                    this.searchNewDeviceDealerTimer = null;
                    return;
                }
                this.filteredNewDeviceDealers = this.dealers
                    .filter(dealer => dealer.first_name && dealer.first_name.toLowerCase().includes(searchTerm))
                    .slice(0, 50);
                this.searchNewDeviceDealerTimer = null;
            }, 200);
        },

        selectNewDeviceDealer(dealer) {
            this.selectedNewDeviceDealer = dealer;
            this.formData.new_device_dealer_id = dealer.id;
            this.newDeviceDealerSearch = dealer.first_name;
            this.showNewDeviceDealerSuggestions = false;
        },

        hideNewDeviceDealerSuggestions() {
            setTimeout(() => {
                this.showNewDeviceDealerSuggestions = false;
            }, 200);
        },

        // Form Submission
        submitForm() {
            if (this.isSubmitting) return;

            this.errors = {};
            
            // Validation (device_type only required for add mode)
            if (!this.isEditMode && !this.formData.device_type) {
                this.errors.device_type = "Device type is required";
                return;
            }

            // In edit mode, validate based on what's being updated
            // In add mode, validate based on device_type
            if (!this.isEditMode) {
                // Validate old device if required
                if (this.showOldDevice && !this.formData.old_device_id && !this.formData.old_device_imei) {
                    this.errors.old_device_id = "Old device is required";
                    return;
                }

                // Validate new device if required
                if (this.showNewDevice && !this.formData.new_device_id && !this.formData.new_device_imei) {
                    this.errors.new_device_id = "New device is required";
                    return;
                }
            }

            this.isSubmitting = true;
            this.getToken();

            // Build payload - include all expected keys to avoid undefined key errors
            // Send null for nullable fields that aren't being used
            const payload = {
                device_type: this.formData.device_type,
            };

            // Always include these keys (set to null if empty to avoid undefined key errors)
            payload.old_device_id = this.formData.old_device_id || null;
            payload.new_device_id = this.formData.new_device_id || null;
            payload.old_device_imei = this.formData.old_device_imei || null;
            payload.new_device_imei = this.formData.new_device_imei || null;
            payload.dealer_id = this.formData.dealer_id || null;
            payload.new_device_dealer_id = this.formData.new_device_dealer_id || null;
            payload.company_id = this.formData.company_id || null;
            payload.device_name = this.formData.device_name || null;
            payload.received_date = this.formData.received_date || null;
            payload.allocated_date = this.formData.allocated_date || null;

            // For edit mode, add the id
            if (this.isEditMode) {
                payload.id = this.formData.id;
            }

            const apiUrl = this.isEditMode ? '/staffs/updateBuybackDevice' : '/staffs/addBuybackDevice';
            
            this.$axios.post(apiUrl, payload, {
                headers: { Authorization: "Bearer " + this.token }
            })
            .then((res) => {
                this.isSubmitting = false;
                if (res.data.status === 'true' || res.data.status === true) {
                    this.$swal("Success", this.$extractApiMessage(res.data.message) || (this.isEditMode ? 'Buy back device updated successfully' : 'Buy back device added successfully'), "success");
                    this.$router.push({ name: 'buy_back_devices' });
                } else {
                    this.$applyApiFieldErrors(this.errors, res.data);
                    this.$swal("Error", this.$getApiErrorMessage(res.data, "Something went wrong"), "error");
                }
            })
            .catch((err) => {
                this.isSubmitting = false;
                this.$applyApiFieldErrors(this.errors, err);
                this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
            });
        },
    },
};
</script>

